import _ from 'lodash'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { HandledDeliveryFee, HandledFrozenFee, PackageSize, StoreDeliveryFeeType } from 'types/storeDeliveryFee'

const initialState: HandledFrozenFee = {
  package_size: '60',
  frozen_fee_amount: 0,
}

export type UseHandleFrozenFee = {
  frozenFees: HandledFrozenFee[]
  handleFrozenFeeAmount: (sizeIndex: number, event: ChangeEvent<HTMLInputElement>) => void
  handleHasFrozenFee: (event: ChangeEvent<HTMLInputElement>) => void
  hasFrozenFee: boolean
}

const useHandleFrozenFee = (
  deliveryFees: HandledDeliveryFee[],
  PACKAGE_SIZES: PackageSize[],
  storeDeliveryFees: StoreDeliveryFeeType[]
): UseHandleFrozenFee => {
  const [frozenFees, setFrozenFees] = useState<HandledFrozenFee[]>([])
  const [hasFrozenFee, setHasFrozenFee] = useState<boolean>(false)

  const handleFrozenFeeAmount = useCallback(
    (sizeIndex: number, event: ChangeEvent<HTMLInputElement>) => {
      setFrozenFees((prevState) => {
        const nextState = prevState
        let value = event.target.value
        // 空文字であれば0にする
        if (/(?:)/.test(value)) {
          nextState[sizeIndex].frozen_fee_amount = 0
        }
        // 全角数字が存在していれば全て半角数字に変換する
        value = value.replace(/[０-９]/g, (s) => {
          return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
        })
        // 数値であれば数値型に変換して更新する
        if (/^[0-9]+$/.test(value)) {
          nextState[sizeIndex].frozen_fee_amount = Number(value)
        }
        return [...nextState]
      })
    },
    [setFrozenFees]
  )

  const handleHasFrozenFee = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setHasFrozenFee((prevState) => {
        if (!prevState && event.target.id === 'frozen') {
          setFrozenFees([initialState])
          return true
        } else {
          setFrozenFees([])
          return false
        }
      })
    },
    [setHasFrozenFee, setFrozenFees]
  )

  useEffect(() => {
    if (!hasFrozenFee || deliveryFees.length === 0) return
    const packageSizes: PackageSize[] = _.uniq(
      deliveryFees.reduce((prev: PackageSize[], deliveryFee) => {
        return [...prev, ...deliveryFee.sizes.map((size) => size.package_size)]
      }, [])
    )
    const _frozenFees: HandledFrozenFee[] = packageSizes.map((packageSize) => {
      const storeDeliveryFee = storeDeliveryFees.find(
        (storeDeliveryFee) => storeDeliveryFee.package_size === packageSize
      )
      const frozenFeeAmount = storeDeliveryFee?.frozen_fee_amount || 0
      return { package_size: packageSize, frozen_fee_amount: frozenFeeAmount }
    })
    setFrozenFees([..._frozenFees])
  }, [deliveryFees, hasFrozenFee, storeDeliveryFees])

  useEffect(() => {
    if (storeDeliveryFees.length === 0) return
    setHasFrozenFee(storeDeliveryFees.some((storeDeliveryFee) => storeDeliveryFee.frozen_fee_amount > 0))
  }, [storeDeliveryFees])

  return {
    frozenFees,
    handleFrozenFeeAmount,
    handleHasFrozenFee,
    hasFrozenFee,
  }
}

export default useHandleFrozenFee
